"use client";

import {
  Container,
  Title,
  Text,
  Box,
  SimpleGrid,
  Paper,
  Stack,
  Button,
  Select,
  Group,
  Badge,
  Anchor,
  Divider,
  ColorSwatch,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import {
  seasons,
  getComparisonBySlug,
  getRelatedComparisons,
  SeasonData,
} from "../comparisonData";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

type Props = {
  slug: string;
};

export default function ComparisonContent({ slug }: Props) {
  const router = useRouter();
  const t = useTranslations("colorPaletteComparison.detail");
  const comparison = getComparisonBySlug(slug);

  const [seasonASelect, setSeasonASelect] = useState<string | null>(comparison?.seasonA.id || null);
  const [seasonBSelect, setSeasonBSelect] = useState<string | null>(comparison?.seasonB.id || null);

  if (!comparison) return null;

  const { seasonA, seasonB } = comparison;
  const relatedComparisons = getRelatedComparisons(slug, 8);

  const handleCompare = () => {
    if (!seasonASelect || !seasonBSelect || seasonASelect === seasonBSelect) return;
    const [first, second] = [seasonASelect, seasonBSelect].sort((a, b) => a.localeCompare(b));
    router.push(`/color-palette/comparison/${first}-vs-${second}`);
  };

  const seasonOptions = seasons.map(s => ({
    value: s.id,
    label: s.name,
  }));

  // Generate the key difference summary
  const generateDifferenceSummary = () => {
    const tempA = seasonA.temperature;
    const tempB = seasonB.temperature;
    const sameTemp = tempA === tempB;
    const sameParent = seasonA.parent === seasonB.parent;

    if (sameParent) {
      // Same parent season (e.g., Deep Winter vs True Winter)
      const parentName = seasonA.parent.charAt(0).toUpperCase() + seasonA.parent.slice(1);
      if (seasonA.value !== seasonB.value) {
        return t("sameParentSameTempDiff", {
          parent: parentName,
          temp: tempA,
          seasonA: seasonA.name,
          valueA: seasonA.value,
          seasonB: seasonB.name,
          valueB: seasonB.value,
        });
      }
      if (seasonA.chroma !== seasonB.chroma) {
        return t("sameParentDiffChroma", {
          parent: parentName,
          temp: tempA,
          seasonA: seasonA.name,
          chromaA: seasonA.chroma,
          seasonB: seasonB.name,
          chromaB: seasonB.chroma,
        });
      }
      return t("sameParentGeneric", { parent: parentName });
    } else if (sameTemp) {
      // Same temperature, different parents
      return t("sameTempDiffParent", {
        temp: tempA,
        seasonA: seasonA.name,
        parentA: seasonA.parent.charAt(0).toUpperCase() + seasonA.parent.slice(1),
        seasonB: seasonB.name,
        parentB: seasonB.parent.charAt(0).toUpperCase() + seasonB.parent.slice(1),
      });
    } else {
      // Different temperatures
      return t("diffTemp", {
        seasonA: seasonA.name,
        tempA: tempA,
        seasonB: seasonB.name,
        tempB: tempB,
      });
    }
  };

  return (
    <Box py={{ base: "xl", md: "4rem" }}>
      <Container size="md">
        {/* Editorial Hero */}
        <Box ta="center" mb={{ base: "2.5rem", md: "3.5rem" }}>
          <Text
            fz="xs"
            tt="uppercase"
            fw={700}
            mb="md"
            style={{ letterSpacing: "0.15em", color: "#999" }}
          >
            {t("categoryLabel")}
          </Text>
          <Title
            order={1}
            fz={{ base: "2.25rem", md: "3.5rem" }}
            fw={700}
            style={{
              color: "#1a1a1a",
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
            }}
          >
            {seasonA.name}
            <Text component="span" fw={300} style={{ color: "#ccc" }} inherit>{t("vs")}</Text>
            {seasonB.name}
          </Title>
        </Box>

        {/* The Key Difference - Editorial callout */}
        <Paper
          p={{ base: "xl", md: "2rem" }}
          radius="md"
          mb={{ base: "2.5rem", md: "3.5rem" }}
          style={{
            background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          }}
        >
          <Text
            fz="xs"
            tt="uppercase"
            fw={700}
            mb="md"
            style={{ letterSpacing: "0.15em", color: "rgba(255,255,255,0.5)" }}
          >
            {t("keyDifferenceTitle")}
          </Text>
          <Text
            fz={{ base: "md", md: "xl" }}
            fw={500}
            style={{ color: "#fff", lineHeight: 1.7 }}
          >
            {generateDifferenceSummary()}
          </Text>
        </Paper>

        {/* Palette Selector - Minimal */}
        <Paper
          p="md"
          radius="md"
          mb={{ base: "2.5rem", md: "3.5rem" }}
          style={{
            background: "#fff",
            border: "1px solid #e5e5e5",
          }}
        >
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <Select
              placeholder={t("firstPalettePlaceholder")}
              data={seasonOptions}
              value={seasonASelect}
              onChange={setSeasonASelect}
              size="sm"
              radius="sm"
              searchable
              styles={{ input: { border: "1px solid #e5e5e5" } }}
            />
            <Select
              placeholder={t("secondPalettePlaceholder")}
              data={seasonOptions}
              value={seasonBSelect}
              onChange={setSeasonBSelect}
              size="sm"
              radius="sm"
              searchable
              styles={{ input: { border: "1px solid #e5e5e5" } }}
            />
            <Button
              size="sm"
              radius="sm"
              rightSection={<IconArrowRight size={16} />}
              onClick={handleCompare}
              disabled={!seasonASelect || !seasonBSelect || seasonASelect === seasonBSelect}
              color="dark"
              variant="filled"
            >
              {t("compareButton")}
            </Button>
          </SimpleGrid>
        </Paper>

        {/* Side-by-Side Overview */}
        <Box mb={{ base: "3rem", md: "4rem" }}>
          <SectionHeader>{t("atAGlanceTitle")}</SectionHeader>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: "lg", md: "xl" }}>
            <SeasonOverview season={seasonA} t={t} />
            <SeasonOverview season={seasonB} t={t} />
          </SimpleGrid>
        </Box>

        {/* Color Palettes - Editorial Grid */}
        <Box mb={{ base: "3rem", md: "4rem" }}>
          <SectionHeader>{t("palettesTitle")}</SectionHeader>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: "lg", md: "xl" }}>
            <PaletteDisplay season={seasonA} />
            <PaletteDisplay season={seasonB} />
          </SimpleGrid>
        </Box>

        {/* Detailed Characteristics */}
        <Box mb={{ base: "3rem", md: "4rem" }}>
          <SectionHeader>{t("characteristicsTitle")}</SectionHeader>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: "lg", md: "xl" }}>
            <CharacteristicsDetail season={seasonA} />
            <CharacteristicsDetail season={seasonB} />
          </SimpleGrid>
        </Box>

        {/* Who Should Wear */}
        <Box mb={{ base: "3rem", md: "4rem" }}>
          <SectionHeader>{t("whoWearsBestTitle")}</SectionHeader>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: "lg", md: "xl" }}>
            <BestForCard season={seasonA} t={t} />
            <BestForCard season={seasonB} t={t} />
          </SimpleGrid>
        </Box>

        {/* Celebrity Examples */}
        <Box mb={{ base: "3rem", md: "4rem" }}>
          <SectionHeader>{t("spotlightTitle")}</SectionHeader>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: "lg", md: "xl" }}>
            <CelebrityCard season={seasonA} />
            <CelebrityCard season={seasonB} />
          </SimpleGrid>
        </Box>

        <Divider my={{ base: "2rem", md: "3rem" }} color="#e5e5e5" />

        {/* More Comparisons */}
        {relatedComparisons.length > 0 && (
          <Box mb={{ base: "2.5rem", md: "3rem" }}>
            <Text
              fz="xs"
              tt="uppercase"
              fw={700}
              mb="lg"
              style={{ letterSpacing: "0.15em", color: "#999" }}
            >
              {t("moreComparisonsTitle")}
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
              {relatedComparisons.map(related => (
                <Link
                  key={related.slug}
                  href={`/color-palette/comparison/${related.slug}`}
                  style={{ textDecoration: "none" }}
                >
                  <Paper
                    p="md"
                    radius="md"
                    style={{
                      border: "1px solid #e5e5e5",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#1a1a1a";
                      e.currentTarget.style.background = "#FAFAFA";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e5e5";
                      e.currentTarget.style.background = "#fff";
                    }}
                  >
                    <Text fz="sm" fw={500} style={{ color: "#1a1a1a" }}>
                      {related.name}
                    </Text>
                  </Paper>
                </Link>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Related Reading */}
        <Box mb={{ base: "2.5rem", md: "3rem" }}>
          <Text
            fz="xs"
            tt="uppercase"
            fw={700}
            mb="lg"
            style={{ letterSpacing: "0.15em", color: "#999" }}
          >
            {t("continueReadingTitle")}
          </Text>
          <Stack gap="xs">
            <Link
              href={`/color-palette/${seasonA.parent}/${seasonA.id.split("-")[0]}`}
              style={{ fontSize: "14px", color: "#1a1a1a", textDecoration: "underline" }}
            >
              {t("completeGuideLink", { season: seasonA.name })}
            </Link>
            <Link
              href={`/color-palette/${seasonB.parent}/${seasonB.id.split("-")[0]}`}
              style={{ fontSize: "14px", color: "#1a1a1a", textDecoration: "underline" }}
            >
              {t("completeGuideLink", { season: seasonB.name })}
            </Link>
            <Link href="/color-analysis-quiz" style={{ fontSize: "14px", color: "#1a1a1a", textDecoration: "underline" }}>
              {t("quizLink")}
            </Link>
          </Stack>
        </Box>

        {/* CTA - Editorial style */}
        <Paper
          p={{ base: "xl", md: "2.5rem" }}
          radius={0}
          ta="center"
          style={{
            background: "#1a1a1a",
          }}
        >
          <Text
            fz="xs"
            tt="uppercase"
            fw={600}
            mb="sm"
            style={{ letterSpacing: "0.1em", color: "rgba(255,255,255,0.6)" }}
          >
            {t("stillUncertainLabel")}
          </Text>
          <Title order={3} c="white" fw={400} mb="md" fz={{ base: "1.25rem", md: "1.5rem" }}>
            {t("personalizedCtaTitle")}
          </Title>
          <Text c="dimmed" mb="xl" maw={400} mx="auto" fz="sm" style={{ color: "rgba(255,255,255,0.7)" }}>
            {t("personalizedCtaDescription")}
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            size="md"
            radius="sm"
            variant="white"
            color="dark"
            rightSection={<IconArrowRight size={16} />}
          >
            {t("tryAiAnalysisButton")}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}

// Section Header Component
function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Box mb="xl">
      <Text
        fz="xs"
        tt="uppercase"
        fw={700}
        mb="xs"
        style={{ letterSpacing: "0.15em", color: "#999" }}
      >
        {children}
      </Text>
      <Divider color="#e5e5e5" />
    </Box>
  );
}

// Season Overview Component
function SeasonOverview({ season, t }: { season: SeasonData; t: any }) {
  const getTempLabel = (temp: string) => {
    if (temp === "warm") return t("warmLabel");
    if (temp === "cool") return t("coolLabel");
    return t("neutralLabel");
  };

  return (
    <Paper
      p="xl"
      radius="md"
      style={{
        background: "#FAFAFA",
        border: "1px solid #eee",
      }}
    >
      <Title order={3} fz={{ base: "1.25rem", md: "1.5rem" }} fw={600} mb="md" style={{ color: "#1a1a1a" }}>
        {season.name}
      </Title>
      <Text fz="sm" c="dimmed" mb="lg" style={{ lineHeight: 1.8 }}>
        {season.description}
      </Text>
      <Group gap="xs">
        <Badge
          size="md"
          variant="filled"
          color={season.temperature === "warm" ? "orange" : "blue"}
          radius="sm"
        >
          {getTempLabel(season.temperature)}
        </Badge>
        <Badge size="md" variant="light" color="gray" radius="sm">
          {season.value.charAt(0).toUpperCase() + season.value.slice(1)}
        </Badge>
        <Badge size="md" variant="light" color="gray" radius="sm">
          {season.chroma.charAt(0).toUpperCase() + season.chroma.slice(1)}
        </Badge>
      </Group>
    </Paper>
  );
}

// Palette Display Component
function PaletteDisplay({ season }: { season: SeasonData }) {
  return (
    <Box>
      <Title order={4} fz="md" fw={600} mb="lg" style={{ color: "#1a1a1a" }}>
        {season.name}
      </Title>
      <SimpleGrid cols={4} spacing="md">
        {season.colors.map((color, i) => (
          <Stack key={i} gap={8} align="center">
            <ColorSwatch
              color={color.hex}
              size={64}
              radius="md"
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1), inset 0 0 0 1px rgba(0,0,0,0.05)" }}
            />
            <Text fz="xs" c="dimmed" ta="center" fw={500} style={{ lineHeight: 1.3 }}>
              {color.name}
            </Text>
          </Stack>
        ))}
      </SimpleGrid>
    </Box>
  );
}

// Detailed Characteristics Component
function CharacteristicsDetail({ season }: { season: SeasonData }) {
  return (
    <Box>
      <Title order={4} fz="md" fw={600} mb="xl" style={{ color: "#1a1a1a" }}>
        {season.name}
      </Title>
      <Stack gap="xl">
        {season.characteristicDetails.map((detail, i) => (
          <Box key={i}>
            <Text fz="sm" fw={700} mb="xs" style={{ color: "#1a1a1a" }}>
              {detail.title}
            </Text>
            <Text fz="sm" style={{ lineHeight: 1.8, color: "#555" }}>
              {detail.description}
            </Text>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// Best For Card Component
function BestForCard({ season, t }: { season: SeasonData; t: any }) {
  return (
    <Box>
      <Title order={4} fz="md" fw={600} mb="xl" style={{ color: "#1a1a1a" }}>
        {season.name}
      </Title>
      <SimpleGrid cols={1} spacing="lg">
        <Paper p="lg" radius="md" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
          <Group gap="xs" mb="sm">
            <IconCheck size={16} color="#16A34A" />
            <Text fz="xs" fw={700} tt="uppercase" style={{ letterSpacing: "0.1em", color: "#16A34A" }}>
              {t("bestForLabel")}
            </Text>
          </Group>
          <Stack gap={6}>
            {season.bestFor.map((item, i) => (
              <Text key={i} fz="sm" style={{ color: "#166534" }}>
                {item}
              </Text>
            ))}
          </Stack>
        </Paper>
        <Paper p="lg" radius="md" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
          <Group gap="xs" mb="sm">
            <IconX size={16} color="#DC2626" />
            <Text fz="xs" fw={700} tt="uppercase" style={{ letterSpacing: "0.1em", color: "#DC2626" }}>
              {t("avoidLabel")}
            </Text>
          </Group>
          <Stack gap={6}>
            {season.avoid.map((item, i) => (
              <Text key={i} fz="sm" style={{ color: "#991B1B" }}>
                {item}
              </Text>
            ))}
          </Stack>
        </Paper>
      </SimpleGrid>
    </Box>
  );
}

// Celebrity Card Component
function CelebrityCard({ season }: { season: SeasonData }) {
  return (
    <Box>
      <Title order={4} fz="md" fw={600} mb="md" style={{ color: "#1a1a1a" }}>
        {season.name}
      </Title>
      <Group gap="xs">
        {season.celebrities.map((celeb, i) => (
          <Badge key={i} size="lg" variant="light" color="gray" radius="sm" style={{ fontWeight: 500 }}>
            {celeb}
          </Badge>
        ))}
      </Group>
    </Box>
  );
}
